/**
 * DriveZone — Search Store (Zustand)
 * Holds all search state for both panels.
 */

import { create } from 'zustand'

export interface Disruption {
  severity: 'minor' | 'major' | 'closed'
  title: string
  description: string
}

export interface CityResult {
  name: string
  lat: number
  lng: number
  drive_minutes: number
  drive_km: number
  exact_match: boolean
  disruption: Disruption | null
}

export interface ProximityResult {
  origin: { name: string; lat: number; lng: number }
  results: CityResult[]
  drive_window: string
  total: number
  cached: boolean
  note: string | null
}

export interface ConfirmResult {
  origin: { name: string; lat: number; lng: number }
  destination: { name: string; lat: number; lng: number }
  drive_minutes: number
  drive_km: number
  drive_miles: number
  maps_url: string
  cached: boolean
}

interface SearchState {
  // Proximity panel
  proximityOrigin: string
  proximityMinMinutes: number
  proximityMaxMinutes: number
  proximityResults: ProximityResult | null
  proximityLoading: boolean
  proximityError: string

  // Confirm panel
  confirmOrigin: string
  confirmDestination: string
  confirmResult: ConfirmResult | null
  confirmLoading: boolean
  confirmError: string

  // Quota modal
  showQuotaModal: boolean

  // Actions
  setProximityOrigin: (v: string) => void
  setProximityMinMinutes: (v: number) => void
  setProximityMaxMinutes: (v: number) => void
  setProximityResults: (r: ProximityResult | null) => void
  setProximityLoading: (v: boolean) => void
  setProximityError: (v: string) => void

  setConfirmOrigin: (v: string) => void
  setConfirmDestination: (v: string) => void
  setConfirmResult: (r: ConfirmResult | null) => void
  setConfirmLoading: (v: boolean) => void
  setConfirmError: (v: string) => void

  setShowQuotaModal: (v: boolean) => void
  clearAll: () => void
}

export const useSearchStore = create<SearchState>((set) => ({
  proximityOrigin: '',
  proximityMinMinutes: 40,
  proximityMaxMinutes: 50,
  proximityResults: null,
  proximityLoading: false,
  proximityError: '',

  confirmOrigin: '',
  confirmDestination: '',
  confirmResult: null,
  confirmLoading: false,
  confirmError: '',

  showQuotaModal: false,

  setProximityOrigin:     (v) => set({ proximityOrigin: v }),
  setProximityMinMinutes: (v) => set({ proximityMinMinutes: v }),
  setProximityMaxMinutes: (v) => set({ proximityMaxMinutes: v }),
  setProximityResults:    (r) => set({ proximityResults: r }),
  setProximityLoading:    (v) => set({ proximityLoading: v }),
  setProximityError:      (v) => set({ proximityError: v }),

  setConfirmOrigin:      (v) => set({ confirmOrigin: v }),
  setConfirmDestination: (v) => set({ confirmDestination: v }),
  setConfirmResult:      (r) => set({ confirmResult: r }),
  setConfirmLoading:     (v) => set({ confirmLoading: v }),
  setConfirmError:       (v) => set({ confirmError: v }),

  setShowQuotaModal: (v) => set({ showQuotaModal: v }),
  clearAll: () => set({
    proximityResults: null, proximityError: '',
    confirmResult: null, confirmError: '',
    showQuotaModal: false,
  }),
}))
